import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaPlus, FaPlug, FaInfoCircle, FaSlack } from 'react-icons/fa'
import { AnimatedContentLoader, Button, LucaIcon } from '@components'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import {
  fetchMcpConnections,
  updateMcpConnection,
  deleteMcpConnection,
  reauthorizeMcpConnection,
  verifyMcpConnection,
  IMcpConnection
} from '@query'
import { AddConnectionDialog } from './AddConnectionDialog'
import { ConnectionCard } from './ConnectionCard'
import { LucaBrandHeading } from './LucaBrandHeading'
import { LucaPageContent, SessionGate, useLucaWidgetSession } from './shared'
import type { ApprovalPatch } from './toolPermissions'

const SLACK_URL =
  'https://slack.com/oauth/v2/authorize?client_id=1163355496225.12069678022567&scope=app_mentions:read,assistant:write,channels:history,chat:write,commands,files:read,groups:history,im:history,im:read,im:write,reactions:write,users:read&user_scope='

const withoutMcpOAuthParams = (href = window.location.href) => {
  const url = new URL(href)
  url.searchParams.delete('mcp_connected')
  url.searchParams.delete('mcp_error')
  return url
}

export const LucaConnectionsPage: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug, session, sessionLoading, ready } = useLucaWidgetSession()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const connectionsKey = [slug, 'luca-mcp-connections']

  const {
    data: connections,
    isLoading,
    isError
  } = useQuery<IMcpConnection[]>({
    queryKey: connectionsKey,
    queryFn: () => fetchMcpConnections(session),
    enabled: ready
  })

  useEffect(() => {
    const href = window.location.href
    const cleaned = withoutMcpOAuthParams(href)
    if (cleaned.href === href) return

    const params = new URL(href).searchParams
    if (params.has('mcp_connected')) {
      toast.success(t('lucaSettings.connectedToast'))
      queryClient.invalidateQueries({ queryKey: connectionsKey })
    } else {
      toast.error(t('lucaSettings.errorToast'))
    }
    window.history.replaceState({}, '', `${cleaned.pathname}${cleaned.search}`)
  }, [])

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateMcpConnection(session, id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: connectionsKey }),
    onError: () => toast.error(t('lucaSettings.updateError'))
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMcpConnection(session, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionsKey })
      toast.success(t('lucaSettings.deleteSuccess'))
    },
    onError: () => toast.error(t('lucaSettings.deleteError'))
  })

  const verifyMutation = useMutation({
    mutationFn: (id: string) => verifyMcpConnection(session, id),
    onSuccess: ({ verification }) => {
      queryClient.invalidateQueries({ queryKey: connectionsKey })
      if (verification.ok) {
        toast.success(t('lucaSettings.verify.ok'))
      } else {
        toast.error(t(`lucaSettings.verify.${verification.code}`))
      }
    },
    onError: () => toast.error(t('lucaSettings.updateError'))
  })

  const loadToolsMutation = useMutation({
    mutationFn: (id: string) => verifyMcpConnection(session, id),
    onSuccess: ({ verification }) => {
      queryClient.invalidateQueries({ queryKey: connectionsKey })
      if (verification.ok) {
        toast.success(t('lucaSettings.approval.toolsLoaded'))
      } else {
        toast.error(t(`lucaSettings.verify.${verification.code}`))
      }
    },
    onError: () => toast.error(t('lucaSettings.approval.saveError'))
  })

  const approvalMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ApprovalPatch }) =>
      updateMcpConnection(session, id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionsKey })
      toast.success(t('lucaSettings.approval.saved'))
    },
    onError: () => toast.error(t('lucaSettings.approval.saveError'))
  })

  const reconnect = async (connection: IMcpConnection) => {
    try {
      const { authorize_url } = await reauthorizeMcpConnection(session, connection.id, {
        return_url: withoutMcpOAuthParams().toString()
      })
      window.location.assign(authorize_url)
    } catch {
      toast.error(t('lucaSettings.updateError'))
    }
  }

  const items = connections ?? []
  const showEmpty = !isError && !isLoading && items.length === 0
  const addButton = (
    <Button
      intent="primary"
      icon={<FaPlus />}
      onClick={() => setDialogOpen(true)}
      disabled={!ready}
    >
      {t('lucaSettings.addConnection')}
    </Button>
  )

  return (
    <SettingsSubPageLayout
      title={<LucaBrandHeading>{t('settings.sections.lucaConnections')}</LucaBrandHeading>}
    >
      <LucaPageContent>
        {!ready ? (
          <SessionGate loading={sessionLoading} hasBusiness={Boolean(slug)} />
        ) : (
          <>
            <SlackCard>
              <SettingsDataCard
                icon={<LucaIcon />}
                title={t('lucaSettings.slack.title')}
                body={
                  <SlackBody>
                    <SlackDescription>{t('lucaSettings.slack.description')}</SlackDescription>
                    <SlackActions>
                      <SlackHint id="luca-slack-hint">{t('lucaSettings.slack.hint')}</SlackHint>
                      <Button
                        type="button"
                        intent="primary"
                        icon={<FaSlack aria-hidden />}
                        aria-describedby="luca-slack-hint"
                        onClick={() => window.location.assign(SLACK_URL)}
                      >
                        {t('lucaSettings.slack.button')}
                      </Button>
                    </SlackActions>
                  </SlackBody>
                }
              />
            </SlackCard>
            <Intro>
              <ConnIntro>{t('lucaSettings.subtitle')}</ConnIntro>
              <InfoLine>
                <FaInfoCircle size={13} />
                {t('lucaSettings.infoNote')}
              </InfoLine>
            </Intro>

            {isError && <Message>{t('lucaSettings.loadError')}</Message>}

            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={showEmpty}
              isEmptyContent={
                <EmptyState>
                  <EmptyIcon>
                    <FaPlug />
                  </EmptyIcon>
                  <EmptyTitle>{t('lucaSettings.emptyTitle')}</EmptyTitle>
                  <EmptyBody>{t('lucaSettings.emptyBody')}</EmptyBody>
                  {addButton}
                </EmptyState>
              }
            >
              <List>
                {items.map(connection => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    toggling={
                      toggleMutation.isPending && toggleMutation.variables?.id === connection.id
                    }
                    deleting={
                      deleteMutation.isPending && deleteMutation.variables === connection.id
                    }
                    testing={verifyMutation.isPending && verifyMutation.variables === connection.id}
                    loadingTools={
                      loadToolsMutation.isPending && loadToolsMutation.variables === connection.id
                    }
                    savingApproval={
                      approvalMutation.isPending && approvalMutation.variables?.id === connection.id
                    }
                    onToggle={enabled => toggleMutation.mutate({ id: connection.id, enabled })}
                    onReconnect={() => reconnect(connection)}
                    onDisconnect={() => deleteMutation.mutate(connection.id)}
                    onTest={() => verifyMutation.mutate(connection.id)}
                    onSaveApproval={patch => approvalMutation.mutate({ id: connection.id, patch })}
                    onLoadTools={() => loadToolsMutation.mutate(connection.id)}
                  />
                ))}
              </List>
              <AddRow>{addButton}</AddRow>
            </AnimatedContentLoader>
          </>
        )}
      </LucaPageContent>

      {isDialogOpen && (
        <AddConnectionDialog
          session={session}
          returnUrl={withoutMcpOAuthParams().toString()}
          onClose={() => setDialogOpen(false)}
          onCreated={() => {
            setDialogOpen(false)
            queryClient.invalidateQueries({ queryKey: connectionsKey })
          }}
        />
      )}
    </SettingsSubPageLayout>
  )
}

const SlackCard = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const SlackBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const SlackDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

const SlackActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  button {
    flex-shrink: 0;
  }
`

const SlackHint = styled.p`
  margin: 0;
  flex: 1 1 16rem;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const ConnIntro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.luca.textMuted};
  max-width: 62ch;
`

const InfoLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Message = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem 0;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const AddRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xxl}rem ${({ theme }) => theme.spacing.lg}rem;
  border: 1px dashed ${({ theme }) => theme.luca.tintBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.luca.mist};
`

const EmptyIcon = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.luca.deepBlue};
  background: ${({ theme }) => theme.luca.tint};
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  margin: 0;
  color: ${({ theme }) => theme.luca.navy};
`

const EmptyBody = styled.p`
  color: ${({ theme }) => theme.luca.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin: 0 0 ${({ theme }) => theme.spacing.sm}rem;
  max-width: 26rem;
`
